// src/lib/menage-journee-interventions.js
//
// LES INTERVENTIONS DANS LA JOURNÉE DE L'AGENT — règles PURES.
//
// Une intervention est une tâche qui ne vient d'AUCUNE réservation : livraison
// de linge, état des lieux, attente d'un artisan. Elle occupe l'agent autant
// qu'un ménage, et jusqu'ici elle ne lui parvenait pas : l'orchestrateur la
// posait depuis sa frise, elle vivait en base, et l'écran de Sofia ne la lisait
// pas. Elle était donc écrite, visible de deux écrans, et invisible de la seule
// personne censée s'en occuper.
//
// ⚠️ UNE SEULE LISTE, PAS DEUX (décision Dimitri 2026-08-12). Sa journée est une
// suite d'endroits où aller, pas deux listes à recouper devant une porte. Les
// interventions se mêlent donc aux ménages, à leur heure.
//
// ⚠️ CE QUE CE MODULE NE FAIT PAS : il ne RÉORDONNE JAMAIS les ménages entre
// eux. Ils arrivent dans un ordre décidé ailleurs, et « 2e ménage » s'appuie
// dessus ; les retrier ici ferait diverger le titre d'une carte et sa place
// dans la pile. On INSÈRE les interventions dans un ordre existant, on ne
// reconstruit pas l'ordre.
//
// AUCUN MONTANT n'entre ici — la table n'en porte aucun, et l'écran agent est
// cloisonné hors argent.

const COMPLETED = 'completed'
const SKIPPED = 'skipped'

/** Une intervention est-elle faite ? */
export function estFaite(intervention) {
  return intervention?.statut === COMPLETED
}

/**
 * L'heure d'une intervention, en minutes depuis minuit — ou `null`.
 *
 * ⚠️ `null` N'EST PAS UNE ERREUR, c'est un cas courant et voulu : une livraison
 * peut n'avoir aucune heure imposée. Lui en inventer une ferait courir l'agent
 * après un horaire qui n'existe pas.
 */
export function heureInterventionMin(intervention) {
  const brut = String(intervention?.heure_fixee || '').trim()
  const m = /^(\d{1,2}):(\d{2})/.exec(brut)
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  if (!Number.isFinite(h) || !Number.isFinite(min) || h > 23 || min > 59) return null
  return h * 60 + min
}

/**
 * CE QUE LA CARTE ÉCRIT À LA PLACE DE L'HEURE quand il n'y en a pas.
 *
 * « dans la journée » dit la vérité — c'est à faire aujourd'hui, sans créneau.
 * Un tiret ou une case vide laisserait croire à une donnée manquante.
 */
export const SANS_HEURE = 'dans la journée'

/**
 * FUSION DE LA JOURNÉE — une seule liste chronologique.
 *
 * Les ménages GARDENT leur ordre d'entrée (cf. en-tête). Chaque intervention
 * horodatée se glisse avant le premier ménage qui commence plus tard qu'elle ;
 * celles sans heure vont en fin de journée — elles n'ont pas de rendez-vous, ce
 * serait mentir que de les intercaler quelque part.
 *
 * @param {object} opts
 * @param {Array} opts.runs           ménages, DANS L'ORDRE D'AFFICHAGE voulu
 * @param {Array} opts.interventions  interventions du jour
 * @param {(run:any)=>number|null} opts.heureDeRun  heure effective d'un ménage, en minutes
 * @returns {Array<{type:'menage'|'intervention', item:any, rangMenage:number|null}>}
 */
export function fusionnerJournee({ runs, interventions, heureDeRun } = {}) {
  const lesRuns = Array.isArray(runs) ? runs : []
  const heure = typeof heureDeRun === 'function' ? heureDeRun : () => null

  // `skipped` n'existe pas pour l'agent : une intervention annulée n'est pas un
  // endroit où aller. Même traitement que les ménages `skipped`.
  const lesInter = (Array.isArray(interventions) ? interventions : [])
    .filter((it) => it && it.statut !== SKIPPED)

  const horodatees = lesInter
    .filter((it) => heureInterventionMin(it) != null)
    .sort((a, b) => heureInterventionMin(a) - heureInterventionMin(b))
  const sansHeure = lesInter.filter((it) => heureInterventionMin(it) == null)

  // Les ménages d'abord, chacun avec son rang — ⚠️ LE RANG NE COMPTE QUE LES
  // MÉNAGES. Une intervention n'est pas un ménage : l'appeler « 3e ménage »
  // serait faux, et l'agent compte ses ménages, pas ses arrêts.
  const sortie = lesRuns.map((run, i) => ({ type: 'menage', item: run, rangMenage: i }))

  for (const it of horodatees) {
    const h = heureInterventionMin(it)
    let pos = sortie.length
    for (let i = 0; i < sortie.length; i += 1) {
      if (sortie[i].type !== 'menage') continue
      const hRun = heure(sortie[i].item)
      if (Number.isFinite(hRun) && hRun > h) { pos = i; break }
    }
    sortie.splice(pos, 0, { type: 'intervention', item: it, rangMenage: null })
  }

  for (const it of sansHeure) {
    sortie.push({ type: 'intervention', item: it, rangMenage: null })
  }

  return sortie
}

/**
 * LES COMPTEURS DU BANDEAU, interventions COMPRISES.
 *
 * 🔴 POURQUOI ELLES COMPTENT. Un compteur qui annonce « 2 à faire » alors qu'il
 * y a deux ménages ET deux livraisons ment sur la charge de la journée — c'est
 * exactement le défaut des voyageurs partants corrigé la veille. L'agent
 * organise sa journée sur ce chiffre.
 *
 * @param {Array} interventions
 * @returns {{aFaire:number, termine:number, minutes:number}} à ADDITIONNER aux
 *          compteurs des ménages.
 */
export function compteursInterventions(interventions) {
  let aFaire = 0
  let termine = 0
  let minutes = 0
  for (const it of Array.isArray(interventions) ? interventions : []) {
    if (!it || it.statut === SKIPPED) continue
    if (estFaite(it)) termine += 1
    else aFaire += 1
    const m = Number(it.duree_min)
    if (Number.isFinite(m) && m > 0) minutes += m
  }
  return { aFaire, termine, minutes }
}

/**
 * « Faite à 14:32 » — ce que la carte affiche à la place du bouton.
 *
 * L'heure vient de `terminee_le`, posée en base par le trigger depuis le jeton.
 * Sans elle (donnée ancienne, écriture serveur), on dit « Faite » sans mentir
 * sur une heure qu'on ne connaît pas.
 */
export function phraseFaite(intervention) {
  if (!estFaite(intervention)) return null
  const brut = intervention?.terminee_le
  if (!brut) return 'Faite'
  const d = new Date(brut)
  if (Number.isNaN(d.getTime())) return 'Faite'
  const hhmm = new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris',
  }).format(d)
  return `Faite à ${hhmm}`
}
